import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, useMotionValue, useAnimationFrame, useVelocity, useReducedMotion, useInView } from "framer-motion";
import { useLang } from "../../lang";
import { CATALOGS } from "../../catalogs";

function Row({ pages, dir, speed, progress, velocity, active, className }) {
  const track = useRef(null);
  const x = useMotionValue(0);
  const drift = useRef(0);
  const half = useRef(0);
  useEffect(() => {
    const el = track.current;
    const ro = new ResizeObserver(() => { half.current = el.scrollWidth / 2; });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useAnimationFrame((_, delta) => {
    if (!active.current || !half.current) return;
    const boost = 1 + Math.min(Math.abs(velocity.get()) * 2, 1.5);
    drift.current += (Math.min(delta, 50) / 1000) * speed * boost;
    const pos = (drift.current + progress.get() * half.current * 0.35) % half.current;
    x.set(dir > 0 ? -pos : pos - half.current);
  });
  const list = [...pages, ...pages];
  return (
    <motion.div ref={track} style={{ x }} className="flex gap-4 md:gap-6 w-max will-change-transform [backface-visibility:hidden]">
      {list.map((p, i) => (
        <img key={`${p}-${i}`} src={p} alt="" decoding="async" draggable="false" className={`w-auto rounded-lg shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] select-none pointer-events-none ${className}`} />
      ))}
    </motion.div>
  );
}

export default function CatalogRibbon() {
  const { lang } = useLang();
  const ref = useRef(null);
  const k = useReducedMotion() ? 0.2 : 1;
  const inView = useInView(ref, { margin: "200px 0px" });
  const active = useRef(false);
  active.current = inView;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const p = useSpring(scrollYProgress, { stiffness: 90, damping: 30, mass: 0.4 });
  const v = useSpring(useVelocity(scrollYProgress), { stiffness: 40, damping: 30 });
  const tilt = useTransform(p, [0, 1], [26, 8]);
  const camera = useTransform(p, [0, 0.5], [1.15, 1]);
  const title = useTransform(p, [0.15, 0.38], [0, 1]);
  const titleY = useTransform(p, [0.15, 0.45], [40, 0]);
  const [a, b = a] = CATALOGS.map((x) => x.mini);
  const rows = [[...a.slice(0, 6), ...b.slice(0, 6)], [...b.slice(6, 14), ...a.slice(6, 12)], [...a.slice(12), ...b.slice(14, 22)]];

  return (
    <section ref={ref} className="relative h-[85vh] md:h-screen min-h-[560px] bg-[#0b0b0d] overflow-hidden flex items-center" data-testid="home-catalog-ribbon">
      <motion.div style={{ scale: camera }} className="absolute inset-0 [perspective:1400px] flex items-center will-change-transform">
        <motion.div style={{ rotateX: tilt, rotateZ: -5 }} className="w-full flex flex-col gap-4 md:gap-6">
          <div className="opacity-35"><Row pages={rows[0]} dir={1} speed={16 * k} progress={p} velocity={v} active={active} className="h-[15vh] md:h-[21vh]" /></div>
          <div className="opacity-80"><Row pages={rows[1]} dir={-1} speed={26 * k} progress={p} velocity={v} active={active} className="h-[21vh] md:h-[29vh]" /></div>
          <div className="opacity-35"><Row pages={rows[2]} dir={1} speed={20 * k} progress={p} velocity={v} active={active} className="h-[15vh] md:h-[21vh]" /></div>
        </motion.div>
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(11,11,13,0.8)_0%,rgba(11,11,13,0.35)_50%,rgba(11,11,13,0.95)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[16vh] bg-gradient-to-b from-[#0b0b0d] to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[16vh] bg-gradient-to-t from-[#0b0b0d] to-transparent" />
      <motion.div style={{ opacity: title, y: titleY }} className="relative z-10 w-full text-center px-6">
        <div className="text-[12px] uppercase tracking-[0.28em] text-white/60 font-medium">{lang === "it" ? "Cataloghi" : "Catalogues"}</div>
        <h2 className="mt-5 text-[40px] md:text-[80px] lg:text-[104px] font-semibold tracking-[-0.04em] leading-[0.98] text-white">
          {lang === "it" ? <>Sfoglia i nostri<br />cataloghi</> : <>Browse our<br />catalogues</>}
        </h2>
        <div className="mt-10 flex items-center justify-center gap-6 flex-wrap">
          <Link to="/cataloghi" className="rounded-full bg-[#1D3F94] hover:bg-[#2a52b8] text-white px-7 py-3.5 text-[15px] font-medium transition-colors" data-testid="home-ribbon-cta">
            {lang === "it" ? "Sfoglia i cataloghi" : "Browse catalogues"}
          </Link>
        </div>
      </motion.div>
    </section>
  );
}
